import { DollarOutlined, EyeOutlined, LockOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import type { RangePickerProps } from "antd/es/date-picker";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ErrorState from "../../../components/common/ErrorState";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";
import { salesApi } from "../../../services/api";
import { RootState } from "../../../store";
import {
  PaymentMethod,
  PaymentStatus,
  SalesInfo,
  SalesListRequest,
} from "../../../types/sales.types";
const { RangePicker } = DatePicker;
const { Option } = Select;

interface FilterValues {
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs];
  buyerPhone?: string;
  sellerPhone?: string;
  paymentStatus?: string;
}

interface ExtendedSalesInfo extends Omit<SalesInfo, "id"> {
  id: number;
}

const SalesListPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [salesData, setSalesData] = useState<ExtendedSalesInfo[]>([]);
  const [selectedSale, setSelectedSale] = useState<ExtendedSalesInfo | null>(
    null
  );
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>(
    PaymentMethod.CASH
  );
  const [transactionId, setTransactionId] = useState<string>("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [confirmBlockSoldDevicesVisible, setConfirmBlockSoldDevicesVisible] =
    useState(false);
  const [blockSoldDevicesLoading, setBlockSoldDevicesLoading] = useState(false);

  const { user, permissions } = useSelector((state: RootState) => state.auth);
  const { isMobileDevice } = useSelector((state: RootState) => state.ui);
  const { hasPermission } = usePermissions();
  const canBlockSoldDevices = permissions?.find(
    (permission) => permission.name === PERMISSIONS.BLOCK_SOLD_DEVICES.name
  );

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState<FilterValues>({
    dateRange: [dayjs().subtract(30, "days"), dayjs()],
  });

  const [form] = Form.useForm();

  const fetchSalesList = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      setError(null);

      const request: SalesListRequest = {
        pageNumber: page - 1,
        pageSize: pageSize,
        filter: {
          startDate: filters.dateRange?.[0]?.format("DD-MM-YYYY"),
          endDate: filters.dateRange?.[1]?.format("DD-MM-YYYY"),
          buyerPhone: filters.buyerPhone,
          sellerPhone: filters.sellerPhone,
          paymentStatus: filters.paymentStatus,
        },
      };

      const response = await salesApi.getSalesList(request);

      const transformedData: ExtendedSalesInfo[] = (response.data || []).map(
        (sale) => ({
          ...sale,
          id: Number(sale.id),
        })
      );

      setSalesData(transformedData);
      setPagination({
        current: (response.pageNumber || 0) + 1,
        pageSize: response.pageSize || 10,
        total: response.totalElements || 0,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch sales list";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch sales data with initial filters
    fetchSalesList(pagination.current, pagination.pageSize);
  }, [filters]);

  const handleTableChange = (newPagination: any) => {
    fetchSalesList(newPagination.current, newPagination.pageSize);
  };

  const handleFilterSubmit = (values: FilterValues) => {
    setFilters(values);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    form.resetFields();
    setFilters({
      dateRange: [dayjs().subtract(30, "days"), dayjs()],
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchSalesList(1, pagination.pageSize);
  };

  const disabledDate: RangePickerProps["disabledDate"] = (current) => {
    return current && current > dayjs().endOf("day");
  };

  const handleViewSale = (sale: ExtendedSalesInfo) => {
    setSelectedSale(sale);
    setIsViewModalVisible(true);
  };

  const handlePaySale = (sale: ExtendedSalesInfo) => {
    setSelectedSale(sale);
    const dueAmount = sale.totalPayable - sale.paidAmount;
    setPaymentAmount(dueAmount);
    setSelectedPaymentMethod(PaymentMethod.CASH);
    setTransactionId("");
    setIsPaymentModalVisible(true);
  };

  const handleBlockSoldDevices = (sale: ExtendedSalesInfo) => {
    setSelectedSale(sale);
    setConfirmBlockSoldDevicesVisible(true);
  };

  const confirmBlockSoldDevices = async () => {
    if (!selectedSale) return;

    try {
      setBlockSoldDevicesLoading(true);

      const response = await salesApi.blockSoldDevices({
        id: selectedSale.id,
      });

      if (response.status === "SUCCESS") {
        message.success("Devices blocked successfully");
        setConfirmBlockSoldDevicesVisible(false);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to block devices";
      message.error(errorMessage);
    } finally {
      setBlockSoldDevicesLoading(false);
    }
  };

  const handleMakePayment = async () => {
    if (!selectedSale) return;

    try {
      setPaymentLoading(true);

      const paymentData = {
        salesId: selectedSale.id,
        amount: paymentAmount,
        paymentMethod: selectedPaymentMethod,
        transactionId: transactionId || undefined,
      };

      const response = await salesApi.makeSalesPayment(paymentData);

      if (response.status === "SUCCESS") {
        message.success("Payment successful");
        setIsPaymentModalVisible(false);

        // Update the sales list
        fetchSalesList(pagination.current, pagination.pageSize);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to make payment";
      message.error(errorMessage);
    } finally {
      setPaymentLoading(false);
    }
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (typeof value !== "number") return "0";
    return value?.toLocaleString() || "0";
  };

  const getDueAmount = (sale: ExtendedSalesInfo): number => {
    return (sale?.totalPayable || 0) - (sale?.paidAmount || 0);
  };

  const getStatusColor = (status: string) => {
    return status === PaymentStatus.PAID ? "success" : "error";
  };

  const getStatusText = (status: string) => {
    return status || PaymentStatus.DUE;
  };

  const renderItem = (item: ExtendedSalesInfo) => {
    const dueAmount = getDueAmount(item);
    const isDue = dueAmount > 0;

    return (
      <List.Item
        key={item.id}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        <div style={{ width: "100%" }}>
          <List.Item.Meta
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <Tag
                    color={getStatusColor(
                      item.paymentStatus || PaymentStatus.DUE
                    )}
                  >
                    {getStatusText(item.paymentStatus || PaymentStatus.DUE)}
                  </Tag>
                  <Typography.Text type="secondary">
                    {item.transactionDate}
                  </Typography.Text>
                </Space>
                <Space>
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewSale(item)}
                    title="View Details"
                    style={{ padding: '0 4px', boxShadow: 'none' }}
                  >
                    {!isMobileDevice && "View"}
                  </Button>
                  {isDue && (
                    <Button
                      type="text"
                      icon={<DollarOutlined />}
                      onClick={() => handlePaySale(item)}
                      title="Pay Due"
                      style={{ color: "#52c41a", padding: '0 8px', boxShadow: 'none' }}
                    >
                      {!isMobileDevice && "Pay"}
                    </Button>
                  )}
                  {canBlockSoldDevices && (
                    <Button
                      type="text"
                      danger
                      icon={<LockOutlined />}
                      onClick={() => handleBlockSoldDevices(item)}
                      title="Block Sold Devices"
                      style={{ padding: '0 4px', boxShadow: 'none' }}
                    >
                      {!isMobileDevice && "Block"}
                    </Button>
                  )}
                </Space>
              </div>
            }
            description={
              <Space direction="vertical" size={4}>
                <div>
                  <Typography.Text strong>Buyer: </Typography.Text>
                  <span>{item.buyerName || "N/A"}</span>
                  {item.buyerOrganization && (
                    <span style={{ marginLeft: 8 }}>
                      ({item.buyerOrganization})
                    </span>
                  )}
                </div>
                <div>Phone: {item.buyerPhone || "N/A"}</div>
                {user?.designation?.toUpperCase() === "ADMIN" && (
                  <div>
                    <Typography.Text strong>Seller: </Typography.Text>
                    <span>{item.sellerName || "N/A"}</span>
                    {item.sellerOrganization && (
                      <span style={{ marginLeft: 8 }}>
                        ({item.sellerOrganization})
                      </span>
                    )}
                    <div>Phone: {item.sellerPhone || "N/A"}</div>
                  </div>
                )}
                <div>
                  <Space size="large">
                    <div>
                      Total:{" "}
                      <Typography.Text strong>
                        ৳{formatNumber(item.totalPayable)}
                      </Typography.Text>
                    </div>
                    <div>
                      Paid:{" "}
                      <Typography.Text strong>
                        ৳{formatNumber(item.paidAmount)}
                      </Typography.Text>
                    </div>
                    <div>
                      {isDue ? (
                        <span>
                          Due:{" "}
                          <Typography.Text type="danger" strong>
                            ৳{formatNumber(dueAmount)}
                          </Typography.Text>
                        </span>
                      ) : (
                        <span>
                          Due:{" "}
                          <Typography.Text type="success">৳0</Typography.Text>
                        </span>
                      )}
                    </div>
                  </Space>
                </div>
              </Space>
            }
          />
        </div>
      </List.Item>
    );
  };

  if (error) {
    return (
      <ErrorState
        error={error}
        onRetry={() => fetchSalesList(pagination.current, pagination.pageSize)}
      />
    );
  }

  return (
    <>
      <Card title="Sales List">
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Form
            form={form}
            onFinish={handleFilterSubmit}
            initialValues={{
              dateRange: [dayjs().subtract(30, "days"), dayjs()],
            }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="dateRange" label="Date Range">
                  <RangePicker
                    style={{ width: "100%" }}
                    disabledDate={disabledDate}
                    format="DD-MM-YYYY"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="buyerPhone" label="Buyer Phone">
                  <Input
                    placeholder="Search by buyer phone"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              {user?.designation?.toUpperCase() == "ADMIN" && (
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="sellerPhone" label="Seller Phone">
                    <Input
                      placeholder="Search by seller phone"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="paymentStatus" label="Payment Status">
                  <Select
                    placeholder="Select payment status"
                    allowClear
                    style={{ width: "100%" }}
                  >
                    <Option value={PaymentStatus.PAID}>Paid</Option>
                    <Option value={PaymentStatus.DUE}>Due</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row justify="end" gutter={8}>
              <Col>
                <Button onClick={handleReset}>Reset</Button>
              </Col>
              <Col>
                <Button type="primary" htmlType="submit">
                  Filter
                </Button>
              </Col>
            </Row>
          </Form>

          <List
            itemLayout="horizontal"
            dataSource={salesData}
            loading={loading}
            pagination={{
              ...pagination,
              onChange: (page, pageSize) =>
                handleTableChange({
                  current: page,
                  pageSize,
                  total: pagination.total,
                }),
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} items`,
              style: { marginBottom: 16 },
              position: "bottom",
            }}
            renderItem={renderItem}
            style={{
              backgroundColor: "#fff",
              borderRadius: 8,
              padding: "16px 24px",
            }}
          />
        </Space>
      </Card>

      {/* View Sale Details Modal */}
      <Modal
        title="Sale Details"
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {selectedSale && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Transaction Date">
              {selectedSale.transactionDate}
            </Descriptions.Item>
            <Descriptions.Item label="Buyer Name">
              {selectedSale.buyerName || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Buyer Phone">
              {selectedSale.buyerPhone || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Buyer Organization">
              {selectedSale.buyerOrganization || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Seller Name">
              {selectedSale.sellerName || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Seller Phone">
              {selectedSale.sellerPhone || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Seller Organization">
              {selectedSale.sellerOrganization || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Units Sold">
              {formatNumber(selectedSale.unitsSold)}
            </Descriptions.Item>
            <Descriptions.Item label="Unit Price">
              ৳{formatNumber(selectedSale.unitPrice)}
            </Descriptions.Item>
            <Descriptions.Item label="Total Payable">
              ৳{formatNumber(selectedSale.totalPayable)}
            </Descriptions.Item>
            <Descriptions.Item label="Paid Amount">
              ৳{formatNumber(selectedSale.paidAmount)}
            </Descriptions.Item>
            <Descriptions.Item label="Due Amount">
              ৳{formatNumber(getDueAmount(selectedSale))}
            </Descriptions.Item>
            <Descriptions.Item label="Payment Method">
              {selectedSale.paymentMethod || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Transaction ID">
              {selectedSale.transactionId || "-"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Payment Modal */}
      <Modal
        title="Make Payment"
        open={isPaymentModalVisible}
        onCancel={() => setIsPaymentModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsPaymentModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="pay"
            type="primary"
            onClick={handleMakePayment}
            loading={paymentLoading}
          >
            Pay
          </Button>,
        ]}
      >
        {selectedSale && (
          <Form layout="vertical">
            <Form.Item label="Sale ID">
              <Input value={selectedSale.id} disabled />
            </Form.Item>
            <Form.Item label="Buyer">
              <Input value={selectedSale.buyerName} disabled />
            </Form.Item>
            <Form.Item label="Total Payable">
              <Input
                value={`৳${selectedSale.totalPayable?.toLocaleString()}`}
                disabled
              />
            </Form.Item>
            <Form.Item label="Already Paid">
              <Input
                value={`৳${selectedSale.paidAmount?.toLocaleString()}`}
                disabled
              />
            </Form.Item>
            <Form.Item label="Due Amount">
              <Input
                value={`৳${
                  getDueAmount(selectedSale)?.toLocaleString() || "0"
                }`}
                disabled
              />
            </Form.Item>
            <Form.Item
              label="Payment Amount"
              required
              rules={[
                { required: true, message: "Please enter payment amount" },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                value={paymentAmount}
                onChange={(value) => setPaymentAmount(value || 0)}
                min={1}
                max={getDueAmount(selectedSale)}
                formatter={(value) =>
                  `৳ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => {
                  if (!value) return 0;
                  return Number(value.replace(/৳\s?|(,*)/g, ""));
                }}
              />
            </Form.Item>
            <Form.Item label="Payment Method" required>
              <Select
                value={selectedPaymentMethod}
                onChange={(value) => setSelectedPaymentMethod(value)}
                style={{ width: "100%" }}
              >
                {Object.values(PaymentMethod).map((method) => (
                  <Select.Option key={method} value={method}>
                    {method.replace("_", " ")}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            {selectedPaymentMethod !== PaymentMethod.CASH && (
              <Form.Item label="Transaction ID">
                <Input
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  placeholder="Enter transaction ID"
                />
              </Form.Item>
            )}
          </Form>
        )}
      </Modal>

      {/* Confirmation Modal for Reverse Sale */}
      <Modal
        title="Confirm Block Sold Devices"
        open={confirmBlockSoldDevicesVisible}
        onCancel={() => setConfirmBlockSoldDevicesVisible(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setConfirmBlockSoldDevicesVisible(false)}
          >
            Cancel
          </Button>,
          <Button
            key="block"
            type="primary"
            danger
            onClick={confirmBlockSoldDevices}
            loading={blockSoldDevicesLoading}
          >
            Block Sold Devices
          </Button>,
        ]}
      >
        {selectedSale && (
          <div>
            <p>Are you sure you want to block sold devices?</p>
            <p>
              <strong>Buyer:</strong> {selectedSale.buyerName} (
              {selectedSale.buyerPhone})
            </p>
            <p>
              <strong>Tokens:</strong> {selectedSale.unitsSold}
            </p>
            <p>
              <strong>Amount:</strong> ৳
              {selectedSale.paidAmount?.toLocaleString()}
            </p>
            <p>
              <strong>Warning:</strong> This will attempt to block sold devices.
            </p>
          </div>
        )}
      </Modal>
    </>
  );
};

export default SalesListPage;
